import { consume } from "@lit/context";
import { css, html, nothing } from "lit";
import { query } from "lit/decorators.js";
import "../../atom/ClickOutside/index.js";
import "../Dropdown/index.js";
import "../DropdownContent/index.js";
import "../FilterButton/index.js";
import "../SelectButton/index.js";
import { groupContext } from "../FormGroup/index.js";
import { getLabelClasses, getMessageClasses } from "./utils.js";
import { BaseComponent } from "../../BaseComponent.js";
import { prop } from "../../../decorators/prop/index.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { preventDefault } from "../../../decorators/preventDefault.js";
import { testingAttr } from "../../../directives/testingAttr.js";
import { TestingAttrs } from "./customFilter.selectors.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
let DsfCustomFilter = class extends BaseComponent {
  constructor() {
    super();
    this.dsf_open = false;
    this.dsf_flip = true;
    this.dsf_shift = true;
    this.dsf_hide = false;
    this.dsf_show_close_button = false;
    this.dsf_open_on_focus = false;
    this.dsf_size = "medium";
    this.dsf_trigger = "filter-button";
    this.dsf_error = false;
    this._shouldOpenOnFocus = false;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.addEventListener("keydown", this.handleKeydown);
    this.addEventListener("focusout", this.handleFocusOut);
  }
  close() {
    this.closeAndFocus();
  }
  update(changedProps) {
    if (changedProps.has("dsf_open_on_focus")) {
      this._shouldOpenOnFocus = this.dsf_open_on_focus;
    }
    super.update(changedProps);
  }
  updated(changedProps) {
    if (changedProps.has("dsf_open") && this.dsf_open) {
      this.dropdownContent.focusInit();
    }
    super.updated(changedProps);
  }
  focus() {
    this.buttonComponent.focus();
  }
  handleKeydown(ev) {
    const key = ev.key;
    if (!this.dsf_open && ["ArrowUp", "ArrowDown", "Enter", " "].includes(key)) {
      this.toggleDropdown(true);
      ev.preventDefault();
    } else if (this.dsf_open) {
      if (key === "Escape") {
        this.closeAndFocus();
      }
    }
  }
  handleClickOutside() {
    if (this.dsf_open) {
      this.toggleDropdown(false);
    }
  }
  handleFocusOut(ev) {
    const relatedTarget = ev.relatedTarget;
    if (relatedTarget && !this.contains(relatedTarget) && this.dsf_open) {
      this.dropdownContent.focus();
    }
  }
  handleSelectMousedown() {
    this.toggleDropdown(!this.dsf_open);
  }
  handleAnchorFocusIn() {
    if (this._shouldOpenOnFocus) {
      this.toggleDropdown(true);
    }
    this._shouldOpenOnFocus = this.dsf_open_on_focus;
  }
  handleCloseClick() {
    this.closeAndFocus();
  }
  handleButtonClearClick() {
    this.emit("clear-filter");
  }
  handleClearClick() {
    this.emit("clear");
  }
  handleApplyClick() {
    this.emit("apply");
  }
  closeAndFocus() {
    this.toggleDropdown(false);
    this._shouldOpenOnFocus = false;
    this.focus();
  }
  toggleDropdown(open) {
    const oldOpen = this.dsf_open;
    this.dsf_open = open;
    if (oldOpen !== this.dsf_open) {
      this.emit({ name: "open-changed", payload: this.dsf_open });
    }
  }
  renderTrigger() {
    var _a, _b;
    const uiValueFilterButton = this.dsf_display_value ? [this.dsf_display_value] : [];
    if (this.dsf_trigger === "filter-button") {
      return html`<dsf-filter-button
        class="filter-button"
        exportparts="filter-button"
        .dsf_options=${uiValueFilterButton}
        .dsf_open=${this.dsf_open}
        .dsf_size=${this.dsf_size}
        .dsf_placeholder=${this.dsf_placeholder}
        .dsf_icon=${this.dsf_icon}
        @clear-click=${this.handleButtonClearClick}
        ${testingAttr(TestingAttrs.filterButton)}
        ._group_position=${(_a = this.groupCtx) == null ? void 0 : _a.getPosition(this)}
      >
      </dsf-filter-button>`;
    }
    const uiValueSelectButton = this.dsf_display_value ? [{ label: this.dsf_display_value }] : [];
    return html`<dsf-select-button
      class="filter-button"
      exportparts="select-button"
      .dsf_options=${uiValueSelectButton}
      .dsf_open=${this.dsf_open}
      .dsf_size=${this.dsf_size}
      .dsf_placeholder=${this.dsf_placeholder}
      .dsf_error=${this.dsf_error}
      .dsf_icon=${this.dsf_icon}
      dsf_show_clear_icon
      @clear-click=${this.handleButtonClearClick}
      ${testingAttr(TestingAttrs.filterButton)}
      ._group_position=${(_b = this.groupCtx) == null ? void 0 : _b.getPosition(this)}
    >
    </dsf-select-button>`;
  }
  render() {
    const label = this.dsf_label ? html`<label id="label" class=${getLabelClasses(this)} data-skeleton
          >${this.dsf_label}</label
        >` : nothing;
    const message = this.dsf_message ? html`<span class=${getMessageClasses(this)} data-skeleton>${this.dsf_message}</span>` : nothing;
    return html`
      <dsf-click-outside
        @click-outside=${this.handleClickOutside}
        class="flex min-w-0 flex-auto flex-col font-sans text-neutral-700"
      >
        ${label}
        <dsf-dropdown
          class="flex flex-auto flex-col"
          ?dsf_flip=${this.dsf_flip}
          ?dsf_shift=${this.dsf_shift}
          ?dsf_hide=${this.dsf_hide}
          .dsf_open=${this.dsf_open}
          .dsf_offset=${4}
          data-skeleton
        >
          <div
            slot="anchor"
            role="combobox"
            aria-labelledby="label"
            aria-expanded=${this.dsf_open ? "true" : "false"}
            aria-controls="content"
            @mousedown=${this.handleSelectMousedown}
            @focusin=${this.handleAnchorFocusIn}
          >
            ${this.renderTrigger()}
          </div>

          <div
            id="content"
            class="flex flex-auto flex-col overflow-visible rounded-sm border border-neutral-100 bg-neutral-0 shadow-xl"
            part="dropdown"
            role="dialog"
            aria-labelledby="label"
            aria-expanded=${this.dsf_open ? "true" : "false"}
          >
            <dsf-dropdown-content
              id="dropdownContent"
              class="min-h-0"
              .dsf_title_text=${this.dsf_title_text}
              .dsf_show_close_button=${this.dsf_show_close_button}
              .dsf_clear_button_text=${this.dsf_clear_button_text}
              .dsf_apply_button_text=${this.dsf_apply_button_text}
              dsf_custom_content
              @close-click=${this.handleCloseClick}
              @clear-click=${this.handleClearClick}
              @apply-click=${this.handleApplyClick}
              ${testingAttr(TestingAttrs.dropdownContent)}
            >
              <slot></slot>
            </dsf-dropdown-content>
          </div>
        </dsf-dropdown>
        ${message}
      </dsf-click-outside>
    `;
  }
};
DsfCustomFilter.styles = [
  ...BaseComponent.styles,
  css`
      :host {
        display: inline-flex;
        flex: 1 1 auto;
        min-width: 0;
      }

      dsf-dropdown::part(dropdown) {
        z-index: var(--dsf-z-index-dropdown);
      }
    `
];
__decorateClass([
  consume({ context: groupContext, subscribe: true }),
  prop({ attribute: false })
], DsfCustomFilter.prototype, "groupCtx", 2);
__decorateClass([
  prop({ type: String })
], DsfCustomFilter.prototype, "dsf_display_value", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCustomFilter.prototype, "dsf_open", 2);
__decorateClass([
  prop({ type: String })
], DsfCustomFilter.prototype, "dsf_label", 2);
__decorateClass([
  prop({ type: String })
], DsfCustomFilter.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCustomFilter.prototype, "dsf_flip", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCustomFilter.prototype, "dsf_shift", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCustomFilter.prototype, "dsf_hide", 2);
__decorateClass([
  prop({ type: String })
], DsfCustomFilter.prototype, "dsf_title_text", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCustomFilter.prototype, "dsf_show_close_button", 2);
__decorateClass([
  prop({ type: String })
], DsfCustomFilter.prototype, "dsf_clear_button_text", 2);
__decorateClass([
  prop({ type: String })
], DsfCustomFilter.prototype, "dsf_apply_button_text", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCustomFilter.prototype, "dsf_open_on_focus", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfCustomFilter.prototype, "dsf_size", 2);
__decorateClass([
  prop({ type: String })
], DsfCustomFilter.prototype, "dsf_placeholder", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfCustomFilter.prototype, "dsf_icon", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfCustomFilter.prototype, "dsf_trigger", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfCustomFilter.prototype, "dsf_error", 2);
__decorateClass([
  query(".filter-button")
], DsfCustomFilter.prototype, "buttonComponent", 2);
__decorateClass([
  query("dsf-dropdown-content")
], DsfCustomFilter.prototype, "dropdownContent", 2);
__decorateClass([
  preventDefault
], DsfCustomFilter.prototype, "handleCloseClick", 1);
__decorateClass([
  preventDefault
], DsfCustomFilter.prototype, "handleButtonClearClick", 1);
__decorateClass([
  preventDefault
], DsfCustomFilter.prototype, "handleClearClick", 1);
__decorateClass([
  preventDefault
], DsfCustomFilter.prototype, "handleApplyClick", 1);
DsfCustomFilter = __decorateClass([
  defineElement("dsf-custom-filter")
], DsfCustomFilter);
export {
  DsfCustomFilter
};
